/**
 * DEFCON TERMINAL - Dialer Animation System
 *
 * Handles all visual animations for the war dialer interface
 * including progress bars, typing effects, and carrier detection highlights.
 *
 * @module DialerAnimation
 */

import { TIMING, ASCII_DIMENSIONS } from '@shared/constants';
import type { DialResult, PhoneEntry } from './PhoneGenerator';
import { PhoneGenerator } from './PhoneGenerator';

/**
 * Animation frame callback
 */
export type AnimationCallback = (frame: string) => void;

/**
 * Animation completion callback
 */
export type AnimationCompleteCallback = () => void;

/**
 * Progress bar configuration
 */
export interface ProgressBarConfig {
  width: number;
  filledChar: string;
  emptyChar: string;
  showPercentage: boolean;
  showCount: boolean;
}

/**
 * Dialer animation state
 */
export interface DialerAnimationState {
  isAnimating: boolean;
  currentPhoneNumber: string;
  progress: number;
  totalNumbers: number;
  scannedNumbers: number;
  systemsFound: number;
}

/**
 * Default progress bar configuration
 */
const DEFAULT_PROGRESS_CONFIG: ProgressBarConfig = {
  width: 30,
  filledChar: '\u2588',
  emptyChar: '\u2591',
  showPercentage: true,
  showCount: false,
};

/**
 * Dialer animation manager
 */
export class DialerAnimation {
  private state: DialerAnimationState;
  private animationFrameId: number | null = null;
  private readonly config: ProgressBarConfig;

  constructor(config: Partial<ProgressBarConfig> = {}) {
    this.config = { ...DEFAULT_PROGRESS_CONFIG, ...config };
    this.state = {
      isAnimating: false,
      currentPhoneNumber: '',
      progress: 0,
      totalNumbers: 100,
      scannedNumbers: 0,
      systemsFound: 0,
    };
  }

  /**
   * Get current animation state
   */
  getState(): DialerAnimationState {
    return { ...this.state };
  }

  /**
   * Reset animation state
   */
  reset(): void {
    this.stopAnimation();
    this.state = {
      isAnimating: false,
      currentPhoneNumber: '',
      progress: 0,
      totalNumbers: 100,
      scannedNumbers: 0,
      systemsFound: 0,
    };
  }

  /**
   * Stop any running animation
   */
  stopAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.state.isAnimating = false;
  }

  /**
   * Generate progress bar string
   */
  generateProgressBar(progress: number, total: number): string {
    const percentage = Math.min(100, Math.floor((progress / total) * 100));
    const filled = Math.floor((percentage / 100) * this.config.width);
    const empty = this.config.width - filled;

    let bar = this.config.filledChar.repeat(filled) + this.config.emptyChar.repeat(empty);

    if (this.config.showPercentage) {
      bar += `  ${percentage.toString().padStart(3)}%`;
    }

    if (this.config.showCount) {
      bar += `  (${progress}/${total})`;
    }

    return bar;
  }

  /**
   * Animate typing effect for phone number
   */
  async animatePhoneNumber(
    phoneNumber: string,
    callback: AnimationCallback,
    speed: number = TIMING.typingSpeed
  ): Promise<void> {
    this.state.isAnimating = true;
    this.state.currentPhoneNumber = '';

    for (let i = 0; i <= phoneNumber.length; i++) {
      if (!this.state.isAnimating) break;

      this.state.currentPhoneNumber = phoneNumber.slice(0, i);
      callback(this.state.currentPhoneNumber + '_');
      await this.delay(speed);
    }

    callback(phoneNumber);
    this.state.isAnimating = false;
  }

  /**
   * Generate dial result display line
   */
  formatDialResult(entry: PhoneEntry): string {
    const resultDisplay = PhoneGenerator.getResultDisplay(entry.result);

    if (entry.result === 'CARRIER_DETECTED') {
      return `  ${entry.number} ... ${resultDisplay}`;
    }

    return `  ${entry.number} ... ${resultDisplay}`;
  }

  /**
   * Generate carrier detection highlight animation
   */
  async animateCarrierDetection(
    phoneNumber: string,
    callback: AnimationCallback
  ): Promise<void> {
    this.state.isAnimating = true;
    const frames = [
      `  ${phoneNumber} ... \u2588\u2588\u2588\u2588 CARRIER DETECTED \u2588\u2588\u2588\u2588`,
      `  ${phoneNumber} ... \u2591\u2591\u2591\u2591 CARRIER DETECTED \u2591\u2591\u2591\u2591`,
      `  ${phoneNumber} ... \u2588\u2588\u2588\u2588 CARRIER DETECTED \u2588\u2588\u2588\u2588`,
      `  ${phoneNumber} ... \u2591\u2591\u2591\u2591 CARRIER DETECTED \u2591\u2591\u2591\u2591`,
      `  ${phoneNumber} ... \u2588\u2588\u2588\u2588 CARRIER DETECTED \u2588\u2588\u2588\u2588`,
    ];

    for (const frame of frames) {
      if (!this.state.isAnimating) break;
      callback(frame);
      await this.delay(150);
    }

    this.state.isAnimating = false;
  }

  /**
   * Generate connection attempt animation frames
   */
  async animateConnectionAttempt(
    phoneNumber: string,
    callback: AnimationCallback
  ): Promise<void> {
    this.state.isAnimating = true;
    const stages = [
      'DIALING',
      'RINGING',
      'CONNECTING',
      'HANDSHAKE',
    ];

    for (const stage of stages) {
      if (!this.state.isAnimating) break;

      // Animate dots
      for (let dots = 1; dots <= 3; dots++) {
        if (!this.state.isAnimating) break;
        callback(`  ${phoneNumber} ... ${stage}${'.'.repeat(dots)}`);
        await this.delay(200);
      }
    }

    this.state.isAnimating = false;
  }

  /**
   * Generate scanning animation for dialer
   */
  async animateScanning(
    onFrame: (frame: string[]) => void,
    totalNumbers: number = 100
  ): Promise<void> {
    this.state.isAnimating = true;
    this.state.totalNumbers = totalNumbers;

    const spinnerFrames = ['|', '/', '-', '\\'];
    let spinnerIndex = 0;

    while (this.state.isAnimating && this.state.scannedNumbers < totalNumbers) {
      const progressBar = this.generateProgressBar(this.state.scannedNumbers, totalNumbers);
      const spinner = spinnerFrames[spinnerIndex % spinnerFrames.length];

      const frame = [
        `  SCANNING: ${progressBar}`,
        `  ${spinner} Processing...`,
        `  SYSTEMS FOUND: ${this.state.systemsFound}`,
      ];

      onFrame(frame);
      spinnerIndex++;
      await this.delay(100);
    }

    this.state.isAnimating = false;
  }

  /**
   * Update scan progress
   */
  updateProgress(scanned: number, systemsFound: number): void {
    this.state.scannedNumbers = scanned;
    this.state.systemsFound = systemsFound;
    this.state.progress = (scanned / this.state.totalNumbers) * 100;
  }

  /**
   * Generate the full dialer interface frame
   */
  generateDialerFrame(
    dialLog: PhoneEntry[],
    maxLogLines: number = 10
  ): string[] {
    const width = ASCII_DIMENSIONS.terminalWidth;
    const hr = '\u2550'.repeat(width - 2);
    const singleHr = '\u2500'.repeat(45);

    const lines: string[] = [
      `\u2554${hr}\u2557`,
      `\u2551${this.centerText('DEMON DIALER v2.3', width - 2)}\u2551`,
      `\u2551${this.centerText('"SCANNING THE VOID"', width - 2)}\u2551`,
      `\u2560${hr}\u2563`,
      `\u2551${this.padRight('', width - 2)}\u2551`,
      `\u2551  PREFIX: 555-01XX${this.padRight('', width - 20)}\u2551`,
      `\u2551  SCANNING: ${this.generateProgressBar(this.state.scannedNumbers, this.state.totalNumbers)}${this.padRight('', width - 52)}\u2551`,
      `\u2551${this.padRight('', width - 2)}\u2551`,
      `\u2551  DIAL LOG:${this.padRight('', width - 13)}\u2551`,
      `\u2551  ${singleHr}${this.padRight('', width - 49)}\u2551`,
    ];

    // Add dial log entries
    const recentLogs = dialLog.slice(-maxLogLines);
    for (const entry of recentLogs) {
      const logLine = this.formatDialResult(entry);
      lines.push(`\u2551${this.padRight(logLine, width - 2)}\u2551`);
    }

    // Pad with empty lines if needed
    const emptyLines = maxLogLines - recentLogs.length;
    for (let i = 0; i < emptyLines; i++) {
      lines.push(`\u2551${this.padRight('', width - 2)}\u2551`);
    }

    // Footer
    lines.push(`\u2551${this.padRight('', width - 2)}\u2551`);
    lines.push(`\u2551  SYSTEMS FOUND: ${this.state.systemsFound}${this.padRight('', width - 21)}\u2551`);
    lines.push(`\u2551${this.padRight('', width - 2)}\u2551`);
    lines.push(`\u255A${hr}\u255D`);

    return lines;
  }

  /**
   * Generate modem handshake animation
   */
  async animateModemHandshake(callback: AnimationCallback): Promise<void> {
    this.state.isAnimating = true;
    const handshakeFrames = [
      'ATDT...',
      'ATDT... DIAL TONE',
      'ATDT... DIALING',
      'ATDT... RINGING',
      'ATDT... CONNECT',
      'CONNECT 9600',
      'CONNECT 9600 - CARRIER DETECT',
      'CONNECT 9600 - CARRIER DETECT - HANDSHAKE',
      'CONNECT 9600 - CARRIER DETECT - HANDSHAKE COMPLETE',
    ];

    for (const frame of handshakeFrames) {
      if (!this.state.isAnimating) break;
      callback(frame);
      await this.delay(300);
    }

    this.state.isAnimating = false;
  }

  /**
   * Generate static/noise effect for carrier detection
   */
  generateStaticNoise(width: number, height: number): string[] {
    const chars = ['\u2591', '\u2592', '\u2593', '\u2588', ' ', '.', ':'];
    const lines: string[] = [];

    for (let y = 0; y < height; y++) {
      let line = '';
      for (let x = 0; x < width; x++) {
        line += chars[Math.floor(Math.random() * chars.length)];
      }
      lines.push(line);
    }

    return lines;
  }

  /**
   * Helper: Center text within a given width
   */
  private centerText(text: string, width: number): string {
    const padding = Math.max(0, width - text.length);
    const leftPad = Math.floor(padding / 2);
    const rightPad = padding - leftPad;
    return ' '.repeat(leftPad) + text + ' '.repeat(rightPad);
  }

  /**
   * Helper: Pad text to right to fill width
   */
  private padRight(text: string, width: number): string {
    return text + ' '.repeat(Math.max(0, width - text.length));
  }

  /**
   * Helper: Promise-based delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default DialerAnimation;
